import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { AssemblyDecision } from '../../api/schema'
import { useFormatters } from '../../lib/format'
import { Badge } from '../ui/badge'
import { Card, CardContent } from '../ui/card'
import { EmptyState } from '../ui/empty-state'
import { Input } from '../ui/input'
import { AppIcon } from '../ui/icon'
import { CollapsibleText } from './CollapsibleText'
import { VoteBar } from './VoteBar'
import { subjectIcon } from './assembly-icons'
import { useAssetDecisions } from './assembly-api'
import { OUTCOME_FILTERS, RESOLUTION_SUBJECTS, forceTone, outcomeTone, ruleShort } from './assembly-utils'

const LINKS = ['direct', 'ancestor', 'descendant'] as const

type DecisionSearch = { decisionQ?: string; decisionOutcome?: string; decisionSubject?: string; decisionLink?: string }

/**
 * "Was it voted?" on an asset page: the resolutions of the PVs that concern the
 * asset or the building above it, with the date, the majority, the outcome and
 * whether the decision is still in force. Filters live in the URL.
 * Spec 2026-10-10-08 §3, §5.
 */
export function AssetDecisions({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('assemblies')
    const { formatDate } = useFormatters()
    const navigate = useNavigate()
    const search = useSearch({ strict: false }) as DecisionSearch
    const q = search.decisionQ ?? ''
    const outcome = search.decisionOutcome ?? ''
    const subject = search.decisionSubject ?? ''
    const link = search.decisionLink ?? ''
    const { data, isLoading } = useAssetDecisions(org, assetUid, { q, outcome, subject, link })
    const rows = data?.data ?? []
    const filtering = !!(q || outcome || subject || link)

    const setFilter = (key: keyof DecisionSearch, value: string) =>
        navigate({
            search: ((prev: Record<string, unknown>) => ({ ...prev, [key]: value || undefined })) as never,
            replace: true,
        })

    return (
        <div className="space-y-4" data-testid="asset-decisions">
            <div className="space-y-1">
                <h2 className="text-lg font-semibold">{t('decisions.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('decisions.description')}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2" data-testid="asset-decisions-filters">
                <Input
                    className="h-9 max-w-xs"
                    value={q}
                    placeholder={t('decisions.search')}
                    aria-label={t('decisions.search')}
                    onChange={(e) => setFilter('decisionQ', e.target.value)}
                    data-testid="asset-decisions-search"
                />
                <select
                    aria-label={t('decisions.outcome_filter')}
                    value={outcome}
                    onChange={(e) => setFilter('decisionOutcome', e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="asset-decisions-outcome"
                >
                    <option value="">{t('decisions.all_outcomes')}</option>
                    {OUTCOME_FILTERS.map((o) => <option key={o} value={o}>{t(`outcome.${o}`)}</option>)}
                </select>
                <select
                    aria-label={t('decisions.subject_filter')}
                    value={subject}
                    onChange={(e) => setFilter('decisionSubject', e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="asset-decisions-subject"
                >
                    <option value="">{t('decisions.all_subjects')}</option>
                    {RESOLUTION_SUBJECTS.map((s) => <option key={s} value={s}>{t(`subject.${s}`)}</option>)}
                </select>
                <select
                    aria-label={t('decisions.link_filter')}
                    value={link}
                    onChange={(e) => setFilter('decisionLink', e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="asset-decisions-link"
                >
                    <option value="">{t('decisions.all_links')}</option>
                    {LINKS.map((l) => <option key={l} value={l}>{t(`decisions.link.${l}`)}</option>)}
                </select>
                {filtering && data && (
                    <span className="text-xs text-muted-foreground" data-testid="asset-decisions-count">
                        {t('decisions.count', { shown: rows.length, total: data.total })}
                    </span>
                )}
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : rows.length === 0 ? (
                <EmptyState
                    variant="panel"
                    title={filtering ? t('decisions.no_match') : t('decisions.empty')}
                    description={filtering ? undefined : t('decisions.empty_description')}
                    data-testid="asset-decisions-empty"
                />
            ) : (
                <div className="space-y-2" data-testid="asset-decisions-list">
                    {rows.map((d) => (
                        <DecisionCard key={d.resolution_uid} org={org} d={d} formatDate={formatDate} />
                    ))}
                </div>
            )}
        </div>
    )
}

function DecisionCard({
    org,
    d,
    formatDate,
}: {
    org: string
    d: AssemblyDecision
    formatDate: (v: string, o?: Intl.DateTimeFormatOptions) => string
}) {
    const { t } = useTranslation('assemblies')
    return (
        <Card data-testid={`decision-${d.resolution_uid}`} data-force={d.force} data-outcome={d.outcome}>
            <CardContent className="flex flex-wrap items-start gap-3 p-4">
                <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Link
                            to="/$org/assemblies/$uid"
                            params={{ org, uid: d.minutes_uid }}
                            search={{ resolution: d.resolution_uid }}
                            className="font-medium underline-offset-2 hover:underline"
                            data-testid={`decision-link-${d.resolution_uid}`}
                        >
                            {d.resolution_number != null ? `${d.resolution_number}. ` : ''}{d.title}
                        </Link>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span data-testid={`decision-held-${d.resolution_uid}`}>
                            {d.held_at ? t('decisions.held', { date: formatDate(d.held_at, { dateStyle: 'long' }) }) : t('decisions.no_date')}
                        </span>
                        <span>· {d.minutes_name || d.minutes_code}</span>
                        {d.resolution_subject && (
                            <span className="inline-flex items-center gap-1">
                                <AppIcon path={subjectIcon(d.resolution_subject)} size="xs" tone="muted" />
                                {t(`subject.${d.resolution_subject}`, { defaultValue: d.resolution_subject })}
                            </span>
                        )}
                        {ruleShort(d.majority_rule) && (
                            <Badge variant="outline" title={t(`rule.${d.majority_rule}`)} data-testid={`decision-rule-${d.resolution_uid}`}>
                                {ruleShort(d.majority_rule)}
                            </Badge>
                        )}
                        <Badge variant="outline" data-testid={`decision-scope-${d.resolution_uid}`}>{t(`decisions.link.${d.link}`)}</Badge>
                        {!d.reviewed && <Badge variant="warning">{t('decisions.to_review')}</Badge>}
                    </div>
                    {d.force_reason && d.force === 'not_in_force' && (
                        <p className="text-xs text-muted-foreground">{t(`decisions.reason.${d.force_reason}`, { defaultValue: '' })}</p>
                    )}
                    {d.force === 'superseded' && (
                        <p className="text-xs text-muted-foreground" data-testid={`decision-superseded-${d.resolution_uid}`}>{t('decisions.superseded_by')}</p>
                    )}
                    {d.effective_from && d.force !== 'superseded' && (
                        <p className="text-xs text-muted-foreground">{t('decisions.effective_from', { date: formatDate(d.effective_from, { dateStyle: 'long' }) })}</p>
                    )}
                    <CollapsibleText text={d.resolution_text ?? ''} testId={`decision-text-${d.resolution_uid}`} />
                    <div className="flex flex-wrap gap-3 text-xs">
                        <Link
                            to="/$org/assemblies/$uid"
                            params={{ org, uid: d.minutes_uid }}
                            search={{ resolution: d.resolution_uid }}
                            className="text-primary underline-offset-2 hover:underline"
                            data-testid={`decision-open-minutes-${d.resolution_uid}`}
                        >
                            {t('decisions.open_minutes')}
                        </Link>
                        {d.document_uid && (
                            <Link
                                to="/$org/documents/$docUid"
                                params={{ org, docUid: d.document_uid }}
                                search={d.source_page ? ({ page: String(d.source_page) } as never) : undefined}
                                className="text-primary underline-offset-2 hover:underline"
                                data-testid={`decision-open-document-${d.resolution_uid}`}
                            >
                                {t('decisions.open_document')}
                                {d.source_page ? ` · ${t('decisions.page', { page: d.source_page })}` : ''}
                            </Link>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                        <Badge variant={outcomeTone(d.outcome)} data-testid={`decision-outcome-${d.resolution_uid}`}>{t(`outcome.${d.outcome}`)}</Badge>
                        <Badge variant={forceTone(d.force)} data-testid={`decision-force-${d.resolution_uid}`}>{t(`decisions.force.${d.force}`)}</Badge>
                    </div>
                    <VoteBar
                        voicesFor={d.voices_for}
                        voicesAgainst={d.voices_against}
                        voicesAbstain={d.voices_abstain}
                        testId={`decision-bar-${d.resolution_uid}`}
                    />
                </div>
            </CardContent>
        </Card>
    )
}
